import { useQuery } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getV2ApiCompanyIdTravelTripsTripId } from 'src/client/spendesk-api';
import {
  type GetV2ApiCompanyIdTravelTripsTripIdBookingsBookingIdPropertyResponseOK,
  type GetV2ApiCompanyIdTravelTripsTripIdResponseOK,
} from 'src/client/spendesk-api-types';

export type Coordinates = {
  latitude: number;
  longitude: number;
};

export type Country = {
  code: string;
  name: string;
};

export type Address = {
  buildingNumber?: string;
  city: string;
  complement?: string;
  full: string;
  postalCode: string;
  street: string;
};

export type MoneyAmount = {
  amount: number;
  currency: string;
  precision: number;
};

export type Place = {
  name: string;
};

export type Region = {
  name: string;
};

export type OptionalPlace = {
  name?: string;
};

export type OptionalRegion = {
  name?: string;
};

export type RoutePoint = {
  coordinates: Coordinates;
  country: Country;
  countryName: string;
  place: Place;
  placeName: string;
  provider: 'mapBox';
  providerId: string;
  region: Region;
  regionName: string;
};

export type PropertyLocation = {
  address: Address;
  coordinates: Coordinates;
  country: Country;
  place?: OptionalPlace;
  region?: OptionalRegion;
};

export type PropertyPhoto = {
  description?: string;
  url: string;
};

export type Property = {
  checkInDate: string;
  checkOutDate: string;
  location: PropertyLocation;
  name: string;
  photos?: PropertyPhoto[];
};

export type BookingStatus =
  GetV2ApiCompanyIdTravelTripsTripIdBookingsBookingIdPropertyResponseOK['data']['status'];

export type PropertyBooking = NonNullable<
  GetV2ApiCompanyIdTravelTripsTripIdResponseOK['data']['trip']['bookings']
>[number];

export type BudgetState = {
  available: MoneyAmount;
  budgeted: MoneyAmount;
  committed: MoneyAmount;
  committedExceeded: MoneyAmount;
  used: MoneyAmount;
  usedExceeded: MoneyAmount;
};

export type TripBudget = {
  companyId: string;
  creation: string;
  deletion?: string;
  id: string;
  state?: BudgetState;
  tripId: string;
  update?: string;
};

export type TripDetails = {
  active: boolean;
  associatedEntities?: { count?: number };
  route?: RoutePoint[];
  bookings?: PropertyBooking[];
  budget?: TripBudget;
  cardId?: string;
  companyId: string;
  creation: string;
  deletion?: string;
};

export const useTripDetailsQuery = (tripId: string | undefined) => {
  const companyId = useCompanyId();
  return useQuery(
    ['trip', tripId],
    async () => {
      if (!tripId) {
        throw new Error('Trip ID is required');
      }
      const response = await getV2ApiCompanyIdTravelTripsTripId({
        path: { companyId, tripId },
        query: {
          enrichBookings: 'true',
          enrichBudgets: 'true',
        },
      });
      if (response.statusCode !== 200) {
        throw new Error('Failed to fetch trip details');
      }
      return reshapeTripDetails(response.body?.data.trip);
    },
    {
      enabled: Boolean(tripId),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

const reshapeTripDetails = (
  tripDetails: GetV2ApiCompanyIdTravelTripsTripIdResponseOK['data']['trip'],
): TripDetails => {
  return {
    active: tripDetails.active,
    associatedEntities: tripDetails.associatedEntities,
    route: tripDetails.route,
    bookings: tripDetails.bookings,
    budget: tripDetails.budget,
    cardId: tripDetails.cardId,
    companyId: tripDetails.companyId,
    creation: tripDetails.creation,
    deletion: tripDetails.deletion,
  };
};
